import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import normalize from 'json-api-normalizer';
import { JSONApiDataObject, ReviewModel, ReviewResponseModel, UserV2 } from 'Douleutaras/types';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { extractIds } from 'Douleutaras/utilities/redux/normalizeItems';
import { feedbackResponseAdded } from 'Douleutaras/redux/data/settings/feedbackResponseSlice';
import { usersAdded } from 'Douleutaras/redux/data/users/usersSlice';
import { RootState } from 'Douleutaras/redux/store';

const reviewsEndpoint = (sellerId: string) => `api/public/v3/${sellerId}/reviews/`;
const FEEDBACK_ENDPOINT = 'api/public/feedback';
const FEEDBACK_RESPONSE_ENDPOINT = '/api/public/feedback_response';
const EMAIL_SENDPOINT = 'api/public/notify_external_clients_feedback';
const EXTERNAL_FEEDBACK_ENDPOINT = 'api/public/external_feedback';

const feedbacksAdapter = createEntityAdapter<JSONApiDataObject<ReviewModel>>();

interface GetFeedbacksArgs {
  sellerId: string;
  page: string;
  isPinned?: boolean;
}

interface FeedbacksNormalizedEntityList {
  feedback: {
    [index in number]: JSONApiDataObject<ReviewModel>;
  };
  feedbackResponse: {
    [index in number]: JSONApiDataObject<ReviewResponseModel>;
  };
  users: {
    [index in number]: JSONApiDataObject<UserV2>;
  };
}

export const getFeedbacks = createAsyncThunk('user/feedback/get', async ({ sellerId, page, isPinned }: GetFeedbacksArgs, api) => {
  const additionalGetParams: Record<string, string> = {
    'page[number]': page
  };

  if (isPinned !== undefined) {
    additionalGetParams['filter[is_pinned]'] = String(isPinned);
  }

  const endpoint = reviewsEndpoint(sellerId);

  const response = await makeRequest(endpoint, {
    method: 'GET',
    additionalGetParams,
    includes: ['response', 'buyer']
  })(api);

  const normalizedResults = normalize(response.data, { endpoint }) as FeedbacksNormalizedEntityList;

  if (normalizedResults.feedbackResponse) {
    api.dispatch(feedbackResponseAdded(normalizedResults.feedbackResponse));
  }

  if (normalizedResults.users) {
    api.dispatch(usersAdded(normalizedResults.users));
  }

  return {
    results: response.data.data,
    isPinned,
    pinnedId: isPinned && response.data.data.length > 0 ? response.data.data[0].id : undefined,
    count: response.data?.meta?.pagination?.count,
    normalizedResults: normalizedResults.feedback
  };
});

export const postFeedback = createAsyncThunk<{}, Record<string, unknown>>(
  'user/feedback/post',
  async (payload, api) => {
    const response = await makeRequest(FEEDBACK_ENDPOINT, {
      method: 'POST',
      body: payload
    })(api);

    return response;
  }
);

export const postExternalFeedback = createAsyncThunk<{}, Record<string, unknown>>(
  'user/external_feedback/post',
  async (payload, api) => {
    const response = await makeRequest(EXTERNAL_FEEDBACK_ENDPOINT, {
      method: 'POST',
      body: payload
    })(api);

    return response;
  }
);

export const addFeedback = createAsyncThunk('user/feedback_response/add', async (args: { comment: string; feedback_id: string }, api) => {
  const response = await makeRequest(`${FEEDBACK_RESPONSE_ENDPOINT}/`, {
    method: 'POST',
    body: {
      ...args
    }
  })(api);

  const responseData = response.data;
  const responseId = String(responseData.id);

  api.dispatch(feedbackResponseAdded({
    [responseId]: {
      type: 'feedbackResponse',
      id: responseId,
      attributes: {
        comment: args.comment,
        created: new Date().toISOString()
      }
    }
  }));

  return { feedbackId: args.feedback_id, responseId };
});

interface PinReviewArgs {
  sellerId: string;
  reviewId: string;
  isPinned: boolean;
}

export const pinReview = createAsyncThunk('user/feedback/pin', async ({ sellerId, reviewId, isPinned }: PinReviewArgs, api) => {
  const response = await makeRequest(`${reviewsEndpoint(sellerId)}${reviewId}/`, {
    method: 'PATCH',
    body: {
      data: {
        type: 'feedback',
        id: reviewId,
        attributes: {
          isPinned
        }
      }
    }
  })(api);

  return { reviewId, isPinned };
});

export const sendUserFeedbackNotification = createAsyncThunk(
  'user/notify_external_clients_feedback/add',
  async (args: { emails: Array<string> }, api) => {
    const response = await makeRequest(`${EMAIL_SENDPOINT}/`, {
      method: 'POST',
      body: args
    })(api);
  }
);

export const feedbacksSlice = createSlice({
  name: 'feedbackItems',
  initialState: feedbacksAdapter.getInitialState<{
    totalFeedbacks: number;
    currentPageItems: [];
    pinnedItemId: string;
    entities: { [i: number]: JSONApiDataObject<ReviewModel> }
  }>({
    totalFeedbacks: 0,
    currentPageItems: [],
    pinnedItemId: undefined,
    entities: {}
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getFeedbacks.fulfilled, (state, action) => {
      if (action.payload.isPinned) {
        state.pinnedItemId = action.payload.pinnedId;
        feedbacksAdapter.setMany(state, action.payload.normalizedResults || {});

        return;
      }

      feedbacksAdapter.upsertMany(state, action.payload.normalizedResults || {});
      state.totalFeedbacks = action.payload.count;
      state.currentPageItems = extractIds(action.payload.results);
    });
    builder.addCase(addFeedback.fulfilled, (state, action) => {
      const { feedbackId, responseId } = action.payload;
      const feedback = state.entities[feedbackId];

      if (feedback) {
        feedback.relationships = {
          ...feedback.relationships,
          response: {
            data: {
              id: responseId,
              type: 'feedbackResponse'
            }
          }
        };
      }
    });
    builder.addCase(pinReview.fulfilled, (state, action) => {
      const { reviewId, isPinned } = action.payload;

      const unpin = id => {
        const pinnedReview = state.entities[id];

        if (pinnedReview) {
          pinnedReview.attributes.isPinned = false;
          state.pinnedItemId = undefined;
        }
      };

      // This case means that we need just to unpin the existing review
      if (isPinned === false) {
        unpin(reviewId);

        return;
      }

      const targetReview = state.entities[reviewId];
      if (targetReview) {
        // Unpin previously pinned review
        unpin(state.pinnedItemId);
        // Assign new pinned review
        targetReview.attributes.isPinned = isPinned;
        state.pinnedItemId = reviewId;
      }
    });
  }
});

export const feedbacksSelectors = feedbacksAdapter.getSelectors((state: RootState) => state.feedbacks);

export const selectPinnedFeedback = (state: RootState) => {
  const { pinnedItemId } = state.feedbacks;

  if (!pinnedItemId) {
    return undefined;
  }

  return feedbacksSelectors.selectById(state, pinnedItemId);
};
